import {
  Box,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import {
  CheckDone,
  Mail,
  MessageInformation,
  MessageWarning,
  Reject,
} from 'src/components/icon'
import { Sign } from 'src/components/svg'
import { WarningMessage } from 'src/components/WarningMessage/WarningMessage'
import { colors } from 'src/lib/chakra-theme/foundations/colors'
import {
  ExplanationRevisionStatusV2,
  ExpDocRevAgreementFormForSessionInSignFlowFragment,
} from 'src/lib/gql-client'

import { assertNever } from '../../../../utils/assertNever'
import { getNotCompletedOptionalAgreementForms } from '../../utils/explanationDocRevisionUtils'
import {
  getSignStatusMessageKind,
  SignStatusMessageKind,
} from '../../utils/signStatusMessage'
import {
  DocRevAllSignStatuses,
  DocRevSignStatus,
} from '../SignFlowScreen/SignFlowScreen'
import { SignRequiredBadge } from '../SignRequiredBadge/SignRequiredBadge'

type Props = {
  revStatus: ExplanationRevisionStatusV2
  isSignerMemberSet: boolean
  agreementForms: ExpDocRevAgreementFormForSessionInSignFlowFragment[]
  hasCrcField: boolean
  docRevSignStatusesMap: Map<string, DocRevAllSignStatuses>
}

export const SignStatus: React.FC<Props> = ({
  revStatus,
  isSignerMemberSet,
  agreementForms,
  hasCrcField,
  docRevSignStatusesMap,
}) => {
  const isRejected = revStatus === 'AgreementRejected'

  const messageKind = getSignStatusMessageKind({
    revStatus,
    isSignerMemberSet,
    agreementForms,
    docRevSignStatusesMap,
  })

  // 必須同意書の署名が完了し、かつ任意同意書の署名が開始されていない（このまま同意完了できる）状態でのみ、
  // 署名が完了していない任意同意書の未署名セルを警告表示する。署名途中（envelope あり）の場合はメッセージと合わせて警告しない
  const notCompletedOptionalUids = new Set(
    messageKind === 'RequiredCompletedOptionalRemaining'
      ? getNotCompletedOptionalAgreementForms(agreementForms).map(af => af.uid)
      : [],
  )

  if (messageKind === undefined) {
    return null
  }

  return (
    <Box bg="white" p="6" borderRadius="lg">
      <InformationMessage isRejected={isRejected} messageKind={messageKind} />
      <TableContainer color="gray.600" mt="4" ml="6">
        <Table variant="unstyled">
          <Thead>
            <Tr>
              {/* 「同意必須」バッジと文書名が並ぶため、他の列より広めに確保する */}
              <Th fontSize="sm" border="none" minW="240px">
                同意書
              </Th>
              <Th fontSize="sm" border="none" minW="160px">
                患者
              </Th>
              <Th
                fontSize="sm"
                border="none"
                minW="160px"
                w={hasCrcField ? undefined : 'full'}
                textTransform="none"
              >
                Dr
              </Th>
              {hasCrcField && (
                <Th fontSize="sm" border="none" w="full">
                  CRC
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {agreementForms.map(af => (
              <Tr key={af.uid} color="gray.600">
                <Td border="none" fontSize="sm">
                  <HStack align="center" spacing="1.5">
                    <Box
                      minW="2"
                      h="2"
                      bg={isRejected ? 'gray.600' : 'green.700'}
                      borderRadius="full"
                    />
                    <Text wordBreak="break-word">
                      {af.icfDocumentRevision.name}
                    </Text>
                    {af.isPatientConsentRequired && (
                      <Box flexShrink={0}>
                        <SignRequiredBadge />
                      </Box>
                    )}
                  </HStack>
                </Td>
                <Td border="none" fontSize="sm">
                  <SignStatusElement
                    status={
                      docRevSignStatusesMap.get(af.uid)?.Patient ?? 'NotSigned'
                    }
                    isRejected={isRejected}
                    emphasizeNotSigned={notCompletedOptionalUids.has(af.uid)}
                  />
                </Td>
                <Td border="none" fontSize="sm">
                  <SignStatusElement
                    status={
                      docRevSignStatusesMap.get(af.uid)?.Dr ?? 'NotSigned'
                    }
                    isRejected={isRejected}
                    emphasizeNotSigned={notCompletedOptionalUids.has(af.uid)}
                  />
                </Td>
                {hasCrcField && (
                  <Td border="none" fontSize="sm">
                    <SignStatusElement
                      status={
                        docRevSignStatusesMap.get(af.uid)?.CRC ?? 'NotSigned'
                      }
                      isRejected={isRejected}
                      emphasizeNotSigned={notCompletedOptionalUids.has(af.uid)}
                    />
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const InformationMessage: React.FC<{
  isRejected: boolean
  messageKind: SignStatusMessageKind
}> = ({ isRejected, messageKind }) => {
  switch (messageKind) {
    case 'NotStarted':
      return (
        <InformationMessageMain
          isRejected={isRejected}
          message="署名実施バナーより同意書へ署名できます。"
        />
      )
    case 'Signing':
      return (
        <InformationMessageMain
          isRejected={isRejected}
          message="同意書へ署名を実施中です。"
        />
      )
    case 'AllCompleted':
    case 'Completed':
      return (
        <InformationMessageMain
          isRejected={isRejected}
          message="同意書への署名が完了しました。"
        />
      )
    case 'RequiredCompletedOptionalRemaining':
      return (
        <HStack spacing="4" align="center" wrap="wrap">
          <InformationMessageMain
            isRejected={isRejected}
            message="必須同意書への署名が完了しました。"
          />
          <WarningMessage message="署名が完了していない任意同意書があります。このまま同意完了すると、以降は署名できません。" />
        </HStack>
      )
    case 'Rejected':
      return (
        <InformationMessageMain isRejected message="同意拒否されました。" />
      )
    default:
      return assertNever(messageKind)
  }
}

const InformationMessageMain: React.FC<{
  isRejected: boolean
  message: string
}> = ({ message, isRejected }) => {
  return (
    <HStack>
      <Box w="max-content">
        <MessageInformation
          color={isRejected ? colors.gray[600] : colors.green[500]}
        />
      </Box>
      <Box fontWeight="bold">{message}</Box>
    </HStack>
  )
}

const SignStatusElement: React.FC<{
  status: DocRevSignStatus
  isRejected: boolean
  emphasizeNotSigned: boolean
}> = ({ status, isRejected, emphasizeNotSigned }) => {
  switch (status) {
    case 'NotSigned':
      if (!emphasizeNotSigned) {
        return null
      }
      return (
        <HStack color="warning" spacing="1.5">
          <MessageWarning />
          <Text fontSize="sm">未署名</Text>
        </HStack>
      )
    case 'SigningOnScreen':
      return (
        <HStack color="gray.600" spacing="1.5">
          <Sign />
          <Text fontSize="sm">署名中</Text>
        </HStack>
      )
    case 'SigningSendLink':
      return (
        <HStack color="gray.600" spacing="1.5">
          <Mail />
          <Text fontSize="sm">署名中</Text>
        </HStack>
      )
    case 'Signed':
      return (
        <HStack color={isRejected ? 'gray.600' : 'green.600'} spacing="1.5">
          <CheckDone />
          <Text fontSize="sm">署名完了</Text>
        </HStack>
      )
    case 'Rejected':
      return (
        <HStack color="red.500" spacing="1.5">
          <Reject />
          <Text fontSize="sm" fontWeight="bold">
            同意拒否
          </Text>
        </HStack>
      )
    default:
      return assertNever(status)
  }
}
